import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import TweenLite from 'gsap/TweenLite';
import React, {
  MutableRefObject,
  RefObject,
  useRef,
} from 'react';
import styled from 'styled-components';
import { LookBackYearRange } from '../../graphQL/types/shared';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import {latestYear, millisecondsPerSeconds} from '../../Utils';
import {
  SidebarContentContainer,
} from '../Grid';
import usePropsChangeRateLimiter, {
  PropsChangeHandlerInput,
} from '../sectionNav/usePropsChangeRateLimiter';
import {
  defaultVerticalSlideAnimationDuration,
  PolicyDimension,
} from '../Utils';
import CountryProductSpace from './CountryProductSpace';
import DiversificationPaths from './DiversificationPaths';
import ExportBasket from './ExportBasket';
import ExportComplexity from './ExportComplexity';
import ExportGrowthDynamics from './ExportGrowthDynamics';
import GrowthGlobalMarketShare from './GrowthGlobalMarketShare';
import GrowthOpportunities from './GrowthOpportunities';
import HighPotentialProducts from './HighPotentialProducts';
import NewProducts from './NewProducts';
import ProductSpace from './ProductSpace';
import { getSidebarHeight } from './SharedStyling';

const scrollbarMaxWidth = 20;

// Need to do this so that `ScrollToPlugin` is not dropped by the minifier:
/* tslint:disable-next-line:no-unused-expression */
ScrollToPlugin;

//#region Styling
const Root = styled(SidebarContentContainer)`
  overflow: hidden;
`;
const ScrollingContainer = styled.div`
  overflow-y: scroll;
  overflow-x: hidden;
  height: 100%;
  margin-right: ${-scrollbarMaxWidth}px;

  /*
   * Need to ignore mouse scroll/wheel events so that the sidebar only scrolls
   * during slide transition
   */
  pointer-events: none;
`;
//#endregion

interface PropsChangeHandlerExtraInputs {
  elRef: MutableRefObject<HTMLElement | null>;
  sidebarHeight: number;
  isFirstRenderRef: MutableRefObject<boolean>;
}

const performPropsChange = (input: PropsChangeHandlerInput<PropsChangeHandlerExtraInputs, number>) => {
  const {
    nextValue, done,
    extraInputs: {
      elRef: {current: el},
      isFirstRenderRef,
      sidebarHeight: innerSidebarHeight,
    },
  } = input;


  if (el !== null) {
    const scrollAmount = nextValue * innerSidebarHeight;
    // Do not scroll when this sidebar is mounted because the sidebar will
    // either appear with a fading or scrolling animation
    if (isFirstRenderRef.current === true) {

      TweenLite.set(el, {
        scrollTo: {y: scrollAmount},
        onComplete: () => {
          isFirstRenderRef.current = false;
          done();
        },
      });
    } else {

      TweenLite.to(
        el,
        defaultVerticalSlideAnimationDuration / millisecondsPerSeconds,
        {delay: 0.2, scrollTo: {y: scrollAmount}, onComplete: done},
      );
    }
  } else {

    done();
  }

};

interface Props extends SlideElementProps {
  // The index of the first slide of the first section, which can be greater
  // than 0 if the there is a landing page:
  firstContentfulSlideIndex: number;

  slideIndex: number;
  getFluentString: GetString;
  goToNextSlide: () => void;
  goToPrevSlide: () => void;
  countryId: string;
  fluentBundle: FluentBundle;
  zIndex: number;
  yearRange: LookBackYearRange;
  isHelpActive: boolean;
  helpTooltipIndex: number;
  policyDimension?: PolicyDimension;
}

const Sidebar = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const {
    slideIndex, firstContentfulSlideIndex,
    getFluentString, goToNextSlide, goToPrevSlide,
    countryId, fluentBundle, zIndex, yearRange,
    isHelpActive, helpTooltipIndex,
  } = props;
  const relativeSlideIndex = slideIndex - firstContentfulSlideIndex;
  const policyDimension = (props.policyDimension !== undefined)
    ? props.policyDimension : PolicyDimension.BalancedPortfolio;

  const elRef = useRef<HTMLDivElement | null>(null);
  const isFirstRenderRef = useRef<boolean>(true);

  const getExtraInputToPropsChangeHandler =
    (): PropsChangeHandlerExtraInputs => ({elRef, sidebarHeight: getSidebarHeight(), isFirstRenderRef});

  usePropsChangeRateLimiter({
    getExtraInputToPropsChangeHandler,
    value: relativeSlideIndex,
    performPropsChange,
  });

  const exportBasketSlide = (
    <ExportBasket
      onNextSlideClick={goToNextSlide}
      onPrevSlideClick={goToPrevSlide}
      getFluentString={getFluentString}
      countryId={countryId}
      year={latestYear}
      isHelpActive={isHelpActive}
      helpTooltipIndex={helpTooltipIndex}
      fluentBundle={fluentBundle}
    />
  );

  const exportComplexitySlide = (
    <ExportComplexity
      onNextSlideClick={goToNextSlide}
      onPrevSlideClick={goToPrevSlide}
      getFluentString={getFluentString}
      countryId={countryId}
      year={latestYear}
      fluentBundle={fluentBundle}
      isHelpActive={isHelpActive}
      helpTooltipIndex={helpTooltipIndex}
    />
  );

  const exportGrowthDynamicsSlide = (
    <ExportGrowthDynamics
      onNextSlideClick={goToNextSlide}
      onPrevSlideClick={goToPrevSlide}
      getFluentString={getFluentString}
      countryId={countryId}
      fluentBundle={fluentBundle}
      yearRange={yearRange}
      year={latestYear}
      isHelpActive={isHelpActive}
      helpTooltipIndex={helpTooltipIndex}
    />
  );

  const growthGlobalMarketShare = (
    <GrowthGlobalMarketShare
      onNextSlideClick={goToNextSlide}
      onPrevSlideClick={goToPrevSlide}
      getFluentString={getFluentString}
      fluentBundle={fluentBundle}
      countryId={countryId}
      isHelpActive={isHelpActive}
      helpTooltipIndex={helpTooltipIndex}
    />
  );

  const productSpace = (
    <ProductSpace
      onNextSlideClick={goToNextSlide}
      onPrevSlideClick={goToPrevSlide}
      getFluentString={getFluentString}
      countryId={countryId}
      fluentBundle={fluentBundle}
      isHelpActive={isHelpActive}
      helpTooltipIndex={helpTooltipIndex}
    />
  );

  const countryProductSpace = (
    <CountryProductSpace
      onNextSlideClick={goToNextSlide}
      onPrevSlideClick={goToPrevSlide}
      getFluentString={getFluentString}
      countryId={countryId}
      isHelpActive={isHelpActive}
      helpTooltipIndex={helpTooltipIndex}
    />
  );

  const newProducts = (
    <NewProducts
      onNextSlideClick={goToNextSlide}
      onPrevSlideClick={goToPrevSlide}
      getFluentString={getFluentString}
      fluentBundle={fluentBundle}
      countryId={countryId}
      year={latestYear}
      isHelpActive={isHelpActive}
      helpTooltipIndex={helpTooltipIndex}
    />
  );

  const diversificationPaths = (
    <DiversificationPaths
      onNextSlideClick={goToNextSlide}
      onPrevSlideClick={goToPrevSlide}
      getFluentString={getFluentString}
      countryId={countryId}
      year={latestYear}
      fluentBundle={fluentBundle}
      isHelpActive={isHelpActive}
      helpTooltipIndex={helpTooltipIndex}
    />
  );

  const growthOpportunities = (
    <GrowthOpportunities
      onNextSlideClick={goToNextSlide}
      onPrevSlideClick={goToPrevSlide}
      getFluentString={getFluentString}
      fluentBundle={fluentBundle}
      countryId={countryId}
      isHelpActive={isHelpActive}
      helpTooltipIndex={helpTooltipIndex}
    />
  );

  const highPotentialProducts = (
    <HighPotentialProducts
      onNextSlideClick={goToNextSlide}
      onPrevSlideClick={goToPrevSlide}
      getFluentString={getFluentString}
      fluentBundle={fluentBundle}
      countryId={countryId}
      policyDimension={policyDimension}
      year={latestYear}
      isHelpActive={isHelpActive}
      helpTooltipIndex={helpTooltipIndex}
    />
  );

  const style: React.CSSProperties = {
    zIndex,
  };
  return (
    <Root style={style} ref={rootElRef}>
      <ScrollingContainer ref={elRef}>
        {exportBasketSlide}
        {exportComplexitySlide}
        {exportGrowthDynamicsSlide}
        {growthGlobalMarketShare}
        {newProducts}
        {productSpace}
        {countryProductSpace}
        {diversificationPaths}
        {growthOpportunities}
        {highPotentialProducts}
      </ScrollingContainer>
    </Root>
  );

});

export default Sidebar;
